/**
 * ResilienceHeatmap — V22.9 ISO 22301 §8.2.2 evidence view.
 *
 * Renders an Approved-BIA heatmap: rows = departments, cols = RTO bands.
 * Click a cell to open a side-drawer with the BIA list and deep-links.
 * Toggle: All / Business only / Technical only.
 *
 * Mounted as the 4th tab of PerformanceCompliancePage.
 */
import { useEffect, useState, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "./ui/sheet";
import { Skeleton } from "./ui/skeleton";
import { AlertTriangle, ShieldCheck, Activity, FileDown, Sparkles, ExternalLink, RefreshCw } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const BAND_TONE = {
  band_1: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", chip: "bg-rose-100", header: "bg-rose-600 text-white" },
  band_2: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", chip: "bg-amber-100", header: "bg-amber-500 text-white" },
  band_3: { bg: "bg-yellow-50", border: "border-yellow-200", text: "text-yellow-800", chip: "bg-yellow-100", header: "bg-yellow-500 text-white" },
  band_4: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-800", chip: "bg-emerald-100", header: "bg-emerald-600 text-white" },
};

export default function ResilienceHeatmap({ api }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [biaType, setBiaType] = useState("all");
  const [drillDown, setDrillDown] = useState(null); // {dept, band, cell}
  const [exporting, setExporting] = useState(false);

  const fetchHeatmap = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/bia-engine/resilience-heatmap?bia_type=${biaType}`);
      setData(res.data);
    } catch (e) {
      toast.error("Failed to load Resilience Heatmap");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [api, biaType]);

  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      // Print-friendly snapshot — browsers natively render Tailwind print classes
      window.print();
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="resilience-heatmap-loading">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!data) return null;

  const { departments, bands, matrix, summary } = data;
  const empty = summary.total_approved === 0;

  return (
    <div className="space-y-5" data-testid="resilience-heatmap">
      {/* ─── Header strip ─── */}
      <div className="flex flex-wrap items-start justify-between gap-3 print:block">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50 text-[10px]" data-testid="heatmap-badge-v229">V22.9 NEW</Badge>
            <Badge variant="outline" className="text-slate-500 border-slate-200 text-[10px]">ISO 22301 §8.2.2 Evidence</Badge>
          </div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            <Sparkles className="w-4 h-4 text-violet-600" />
            Resilience Heatmap
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            Single-screen view of every approved BIA — Business &amp; Technical — across departments and RTO bands. Auditor-grade evidence for ISO 22301 §8.2.2 surveillance.
          </p>
        </div>

        <div className="flex items-center gap-2 print:hidden">
          {/* BIA type toggle */}
          <div className="inline-flex bg-slate-100 rounded-lg p-1" data-testid="heatmap-type-toggle">
            {[
              { id: "all", label: "All" },
              { id: "business", label: "Business" },
              { id: "technical", label: "Technical" },
            ].map((opt) => (
              <button
                key={opt.id}
                type="button"
                data-testid={`heatmap-toggle-${opt.id}`}
                onClick={() => setBiaType(opt.id)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${biaType === opt.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={fetchHeatmap} data-testid="heatmap-refresh-btn" className="text-xs h-8">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
          </Button>
          <Button variant="outline" size="sm" onClick={handleExportPDF} disabled={exporting} data-testid="heatmap-export-btn" className="text-xs h-8">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> Export PDF
          </Button>
        </div>
      </div>

      {/* ─── Summary tiles ─── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="heatmap-summary-tiles">
        <Card className="border-l-4 border-l-sky-500">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Approved BIAs</div>
                <div className="text-2xl font-bold text-slate-800 mt-0.5" data-testid="heatmap-stat-total">{summary.total_approved}</div>
              </div>
              <ShieldCheck className="w-7 h-7 text-sky-500/70" />
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5">{summary.by_type.business} business &middot; {summary.by_type.technical} technical</div>
          </CardContent>
        </Card>

        <Card className={`border-l-4 ${summary.at_risk_pct > 50 ? "border-l-rose-500" : summary.at_risk_pct > 20 ? "border-l-amber-500" : "border-l-emerald-500"}`}>
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">At-Risk (RTO ≤ 24h)</div>
                <div className="text-2xl font-bold text-slate-800 mt-0.5" data-testid="heatmap-stat-atrisk">{summary.at_risk_count}</div>
              </div>
              <AlertTriangle className={`w-7 h-7 ${summary.at_risk_pct > 50 ? "text-rose-500/70" : summary.at_risk_pct > 20 ? "text-amber-500/70" : "text-emerald-500/70"}`} />
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5">{summary.at_risk_pct}% of approved BIAs in Critical/Urgent bands</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-violet-500">
          <CardContent className="pt-4 pb-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-slate-500 uppercase tracking-wide">Departments</div>
                <div className="text-2xl font-bold text-slate-800 mt-0.5" data-testid="heatmap-stat-depts">{summary.departments_total}</div>
              </div>
              <Activity className="w-7 h-7 text-violet-500/70" />
            </div>
            <div className="text-[10px] text-slate-400 mt-1.5">spanning approved BIAs</div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="p-3 pb-0">
            <CardTitle className="text-xs text-slate-500 uppercase tracking-wide font-medium">Top Critical (lowest RTO)</CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3 pt-1.5">
            {summary.top_critical.length === 0 ? (
              <div className="text-xs text-slate-400 italic">No approved BIAs yet</div>
            ) : (
              <ul className="space-y-0.5 text-[11px] leading-tight" data-testid="heatmap-top-critical">
                {summary.top_critical.slice(0, 3).map((t, i) => (
                  <li key={i} className="flex items-center justify-between gap-1">
                    <span className={`font-mono text-[10px] ${t.is_technical ? "text-violet-700" : "text-sky-700"} flex-shrink-0`}>{t.code || "—"}</span>
                    <span className="text-slate-700 truncate flex-1">{t.name}</span>
                    <span className="text-slate-500 flex-shrink-0">{t.rto_hours}h</span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      {/* ─── Empty state ─── */}
      {empty && (
        <Card>
          <CardContent className="text-center py-12">
            <ShieldCheck className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No approved BIAs yet</p>
            <p className="text-xs text-slate-400 mt-1">The Resilience Heatmap surfaces BIAs once they reach <code>approved</code> status. Run a Process or Technical BIA through the wizard to populate this view.</p>
          </CardContent>
        </Card>
      )}

      {/* ─── The matrix ─── */}
      {!empty && (
        <Card data-testid="heatmap-matrix-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Department × RTO Band Matrix</CardTitle>
            <CardDescription className="text-xs">Approved BIAs only. Click any non-zero cell to drill down. IT-line departments are listed first.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-xs border-collapse" data-testid="heatmap-matrix">
                <thead>
                  <tr>
                    <th className="text-left py-2 px-3 text-slate-600 font-semibold border-b border-slate-200">Department</th>
                    {bands.map((b) => (
                      <th key={b.id} className="py-2 px-3 text-center font-semibold border-b border-slate-200">
                        <div className={`inline-block ${BAND_TONE[b.id].header} text-white text-[10px] font-bold px-2 py-0.5 rounded`}>{b.name}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5">{b.label}</div>
                      </th>
                    ))}
                    <th className="py-2 px-3 text-center text-slate-600 font-semibold border-b border-slate-200">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((dept) => {
                    const row = matrix[dept];
                    const total = bands.reduce((s, b) => s + (row[b.id]?.count || 0), 0);
                    const isIT = dept.toLowerCase().includes("information technology") || dept.toLowerCase().startsWith("it ");
                    return (
                      <tr key={dept} className="hover:bg-slate-50" data-testid={`heatmap-row-${dept.replace(/\s+/g, "-").toLowerCase()}`}>
                        <td className="py-2 px-3 border-b border-slate-100">
                          <div className="flex items-center gap-1.5">
                            {isIT && <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50 text-[9px] px-1 py-0">IT</Badge>}
                            <span className="font-medium text-slate-700">{dept}</span>
                          </div>
                        </td>
                        {bands.map((b) => {
                          const cell = row[b.id];
                          const tone = BAND_TONE[b.id];
                          return (
                            <td key={b.id} className="py-1.5 px-2 text-center border-b border-slate-100">
                              {cell.count > 0 ? (
                                <button
                                  type="button"
                                  data-testid={`heatmap-cell-${dept.replace(/\s+/g, "-").toLowerCase()}-${b.id}`}
                                  onClick={() => setDrillDown({ dept, band: b, cell })}
                                  className={`inline-flex items-center justify-center min-w-[36px] h-7 px-2 rounded-md font-bold text-[13px] ${tone.bg} ${tone.text} ${tone.border} border hover:scale-110 transition-transform cursor-pointer`}
                                  title={`${cell.count} BIA${cell.count > 1 ? "s" : ""} — click to view`}
                                >
                                  {cell.count}
                                </button>
                              ) : (
                                <span className="text-slate-300 text-[11px]">·</span>
                              )}
                            </td>
                          );
                        })}
                        <td className="py-2 px-3 text-center font-bold text-slate-700 border-b border-slate-100">{total}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50">
                    <td className="py-2 px-3 font-semibold text-slate-600">Band Totals</td>
                    {bands.map((b) => {
                      const total = departments.reduce((s, d) => s + (matrix[d]?.[b.id]?.count || 0), 0);
                      return (
                        <td key={b.id} className="py-2 px-3 text-center font-bold text-slate-700" data-testid={`heatmap-band-total-${b.id}`}>{total}</td>
                      );
                    })}
                    <td className="py-2 px-3 text-center font-bold text-slate-800">{summary.total_approved}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="text-[10px] text-slate-400 mt-3">
              Auditor note: only BIAs in status <code>approved</code> are counted. In-flight (<code>first_approved</code>, <code>pending_review</code>) BIAs are excluded — re-run the cascade approval to surface them here. RTO bands match the V22.9 4-band scale wired into <code>calculate_rto_from_matrix</code>.
            </div>
          </CardContent>
        </Card>
      )}

      {/* ─── Drill-down drawer ─── */}
      <Sheet open={!!drillDown} onOpenChange={(o) => !o && setDrillDown(null)}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="heatmap-drilldown-drawer">
          {drillDown && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base flex items-center gap-2">
                  <span className={`${BAND_TONE[drillDown.band.id].chip} ${BAND_TONE[drillDown.band.id].text} text-[10px] font-bold px-2 py-0.5 rounded`}>{drillDown.band.name}</span>
                  {drillDown.dept}
                </SheetTitle>
                <SheetDescription className="text-xs">
                  {drillDown.cell.count} approved {drillDown.cell.count === 1 ? "BIA" : "BIAs"} · {drillDown.cell.business} business · {drillDown.cell.technical} technical
                </SheetDescription>
              </SheetHeader>
              <div className="mt-5 space-y-2" data-testid="heatmap-drilldown-list">
                {drillDown.cell.codes.map((code, idx) => {
                  const name = drillDown.cell.names[idx];
                  const isTech = code.startsWith("IT-");
                  return (
                    <Link
                      key={idx}
                      to="/operational-bia"
                      className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-slate-200 hover:border-violet-300 hover:bg-violet-50/40 transition-colors"
                      data-testid={`drilldown-bia-${code}`}
                      onClick={() => setDrillDown(null)}
                    >
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Badge variant="outline" className={`text-[10px] font-mono ${isTech ? "text-violet-700 border-violet-200 bg-violet-50" : "text-sky-700 border-sky-200 bg-sky-50"}`}>{code || "—"}</Badge>
                        <span className="text-xs text-slate-700 truncate">{name}</span>
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
